import translate from '@/utils/translate';
import type {
  CampaignCreatorRow,
  CreatorDeliverable,
  DeliverableState,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {TagColor} from '@/components/tag/Tag';

export interface AssetsStatus {
  label: string;
  color: TagColor;
}

// Hover groups and the cell pill share this map.
export function tagColorForDeliverableState(state: DeliverableState): TagColor {
  if (state === 'pending') {
    return 'warning';
  }
  if (state === 'accepted' || state === 'posted') {
    return 'success';
  }
  return 'neutral';
}

function maxRoundForState(
  deliverables: CreatorDeliverable[],
  state: DeliverableState
): number | null {
  const rounds = deliverables
    .filter((d) => d.state === state)
    .map((d) => d.round ?? 1);
  return rounds.length ? Math.max(...rounds) : null;
}

function hasSubmissionActivity(row: CampaignCreatorRow): boolean {
  // Counts omit `processing` (still uploading/transcoding) and can miss
  // approved extras capped out of acceptedAssetsCount. Deliverables cover both.
  if (row.deliverables.some((d) => d.state !== 'not_submitted')) {
    return true;
  }

  return (
    (row.pendingAssetsCount ?? 0) > 0 ||
    (row.editsRequestedAssetsCount ?? 0) > 0 ||
    (row.acceptedAssetsCount ?? 0) > 0 ||
    (row.extraAssetsCount ?? 0) > 0
  );
}

// Overdue only when past due and the creator has not submitted anything yet.
// Once there is submission activity, fall through to the workflow status.
export function isEffectivelyOverdue(row: CampaignCreatorRow): boolean {
  return row.isOverdue && !hasSubmissionActivity(row);
}

// Most-actionable status for the pill, resolved most-attention-first:
// Overdue (no submissions) › Ready for review › Awaiting V{n} › Awaiting upload ›
// Ready to post › Posted › Complete. Null when there's nothing to show.
export function deriveAssetsStatus(
  row: CampaignCreatorRow
): AssetsStatus | null {
  const {
    requirementsMet,
    postedState,
    pendingAssetsCount,
    editsRequestedAssetsCount,
    notSubmittedCount,
    deliverables,
    maxRevisionRound,
  } = row;

  if (isEffectivelyOverdue(row)) {
    return {label: translate('Overdue'), color: 'danger'};
  }

  if ((pendingAssetsCount ?? 0) > 0) {
    // Floor at 1: version labels are 1-indexed, so a count/deliverables
    // mismatch that drops the resolved round to 0 must never surface as "V0".
    const round = Math.max(
      maxRoundForState(deliverables, 'pending') ?? maxRevisionRound,
      1
    );
    return {
      label: translate('Ready for review · V{version}', {version: round}),
      color: 'warning',
    };
  }

  if ((editsRequestedAssetsCount ?? 0) > 0) {
    const round = Math.max(
      maxRoundForState(deliverables, 'rejected') ?? maxRevisionRound,
      1
    );
    return {
      label: translate('Awaiting V{version}', {version: round + 1}),
      color: 'neutral',
    };
  }

  if ((notSubmittedCount ?? 0) > 0) {
    return {label: translate('Awaiting upload'), color: 'neutral'};
  }

  if (postedState === 'ready_to_post') {
    return {label: translate('Ready to post'), color: 'neutral'};
  }

  if (postedState === 'posted') {
    return {label: translate('Posted'), color: 'success'};
  }

  // Terminal gigs already resolve to 'Posted' above (postedState mirrors the
  // same terminal-state check), so only a pre-post gig with everything approved
  // reaches Complete.
  if (requirementsMet) {
    return {label: translate('Complete'), color: 'success'};
  }

  return null;
}
